import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useTable, Cell, Row, HeaderGroup, ColumnInstance } from 'react-table'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import CastRoundedIcon from '@mui/icons-material/CastRounded'
import LaunchIcon from '@mui/icons-material/Launch'

import ChipButton from '@/components/ui/buttons/ChipButton'
import Pagination from '@/components/ui/pagination/Pagination'

import { OfflineConsultation } from '@/core/offline/types/database.types'
import { useColumns } from './columns'

type OfflineConsultationsTableProps = {
  consultations: OfflineConsultation[]
  loading?: boolean
  onPageChange?: (page: number) => void
  onRowsPerPageChange?: (rowsPerPage: number) => void
  page?: number
  rowsPerPage?: number
  totalCount?: number
  rowsPerPageOptions?: number[]
}

const defaultRowsPerPageOptions = [5, 10, 15]

export function OfflineConsultationsTable(props: OfflineConsultationsTableProps) {
  const { consultations, onPageChange, onRowsPerPageChange, page = 1, rowsPerPageOptions = defaultRowsPerPageOptions, totalCount = 0 } = props

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  // Transform offline consultations to match the expected format for the table
  const transformedConsultations = consultations.map((consultation) => ({
    _id: consultation._offlineId || consultation._id,
    startDate: consultation.startDate,
    status: consultation.status,
    type: consultation.type,
    patient:
      typeof consultation.patient === 'string'
        ? { userInfo: { name: t('sync.consultations.table.unknownPatient'), surname: '', gender: '' } } // Fallback for string refs
        : consultation.patient,
    _syncStatus: consultation._syncStatus,
    _originalConsultation: consultation,
  }))

  const columns = useColumns()

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    columns,
    data: transformedConsultations,
  })

  const [currentPage, setCurrentPage] = useState<number>(page)

  const handleChangePage = (newPage: number) => {
    if (currentPage === newPage) return
    setCurrentPage(newPage)
    onPageChange?.(newPage)
  }

  const handleOpenConsultationLive = (row: Row<any>) => () => {
    const consultation = row.original._originalConsultation as OfflineConsultation
    const activeStatuses = ['scheduled', 'active', 'accepted']

    if (activeStatuses.includes(consultation.status)) {
      navigate(`/offline/consultations/${consultation._offlineId || consultation._id}/local`)
    } else {
      enqueueSnackbar(t('sync.consultations.table.completedViewComingSoon'), { variant: 'info' })
    }
  }

  const handleOpenConsultationDetails = (row: Row<any>) => () => {
    const consultation = row.original._originalConsultation as OfflineConsultation
    // Navigate to consultation details page
    navigate(`/offline/consultations/${consultation._offlineId || consultation._id}`)
  }

  return (
    <TableContainer component={Paper} id="offline-consultations-table">
      <Table sx={{ minWidth: 650 }} aria-label="offline consultations table">
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<any>) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: HeaderGroup<any> & { align?: TableCellProps['align'] }) => {
                return (
                  <TableCell {...column.getHeaderProps()} scope="col" align={column.align}>
                    {column.render('Header')}
                  </TableCell>
                )
              })}
              <TableCell align="right">{t('common:table.header.action').toUpperCase()}</TableCell>
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            const consultation = row.original._originalConsultation as OfflineConsultation
            const activeStatuses = ['scheduled', 'active', 'accepted']
            const isActive = activeStatuses.includes(consultation.status)

            return (
              <TableRow {...row.getRowProps()} key={row.original._id}>
                {row.cells.map((cell: Cell<any>) => (
                  <TableCell {...cell.getCellProps()} align={(cell.column as ColumnInstance<any> & { align?: TableCellProps['align'] }).align}>
                    {cell.render('Cell')}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Stack justifyContent="flex-end" direction="row" spacing={1}>
                    {isActive && (
                      <Tooltip title={t('sync.consultations.table.openConsultation')}>
                        <ChipButton id="see" color="info" onClick={handleOpenConsultationLive(row)}>
                          <CastRoundedIcon />
                        </ChipButton>
                      </Tooltip>
                    )}
                    <Tooltip title={isActive ? t('sync.consultations.table.openConsultation') : t('sync.consultations.table.viewDetailsComingSoon')}>
                      <ChipButton color="info" onClick={handleOpenConsultationDetails(row)}>
                        <LaunchIcon />
                      </ChipButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={currentPage}
          onPageChange={handleChangePage}
          onItemsPerPageChange={onRowsPerPageChange}
          totalItems={totalCount}
        />
      </Box>
    </TableContainer>
  )
}
